import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

/**
 * Shared Axios instance for all backend API calls.
 *
 * - Automatically attaches JWT Bearer token from auth store
 * - Auto-refreshes token on 401 responses
 * - Logs out if refresh also fails
 *
 * Uses relative URLs (e.g., `/config`, `/auth/login`).
 * The axios baseURL is `/api` so all calls resolve to `/api/...` (H-18).
 * In development, Vite proxy forwards `/api` to backend.
 * In production, the frontend is served from the same origin.
 */

// Module-level token references — updated by authStore on login/refresh/logout
let _accessToken: string | null = null
let _refreshToken: string | null = null
let _onLogout: (() => void) | null = null
let _onPasswordChangeRequired: (() => void) | null = null

/** Called by authStore to update tokens after login or refresh */
export function setTokens(access: string, refresh: string) {
  _accessToken = access
  _refreshToken = refresh
}

/** Called by authStore on logout */
export function clearTokens() {
  _accessToken = null
  _refreshToken = null
}

/** Register logout callback from authStore */
export function onLogout(cb: () => void) {
  _onLogout = cb
}

/** Register callback fired when the backend demands a password change (403 + header) */
export function onPasswordChangeRequired(cb: () => void) {
  _onPasswordChangeRequired = cb
}

/** Get current access token (for use outside React) */
export function getAccessToken(): string | null {
  return _accessToken
}

export const api = axios.create({
  baseURL: '/api',
  timeout: 15000,
})

// Request interceptor: attach JWT Bearer token and default JSON Content-Type for non-FormData
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (_accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${_accessToken}`
  }
  // Set JSON Content-Type by default, but let browser handle FormData
  if (config.headers && config.data instanceof FormData === false) {
    config.headers['Content-Type'] = 'application/json'
  }
  return config
})

// Track pending refresh to avoid concurrent refresh requests
let isRefreshing = false
let pendingRequests: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

function processPendingRequests(error: unknown | null, token: string | null) {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error || !token) {
      reject(error)
    } else {
      resolve(token)
    }
  })
  pendingRequests = []
}

// Response interceptor: handle 401 with automatic token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Forced password change: backend devuelve 403 con la bandera. Puede pasar a
    // mitad de sesión (ej. el admin reseteó la clave del profesional). Marcamos el
    // estado para que AuthLayout redirija a la pantalla de cambio obligatorio.
    if (error.response?.status === 403) {
      const flag =
        (error.response.headers?.['x-password-change-required'] as string | undefined) ||
        ((error.response.data as Record<string, unknown> | undefined)?.code as string | undefined)
      if (flag === 'true' || flag === 'PASSWORD_CHANGE_REQUIRED') {
        _onPasswordChangeRequired?.()
      }
      return Promise.reject(normalizeError(error))
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(normalizeError(error))
    }

    // Endpoints de auth: un 401 acá es semántico (ej. contraseña actual incorrecta
    // en /auth/change-password), NO un token vencido. No intentamos refrescar ni
    // desloguear — dejamos que el error llegue al formulario para mostrarlo.
    const reqUrl = originalRequest.url || ''
    if (/\/auth\/(login|refresh|change-password)$/.test(reqUrl)) {
      return Promise.reject(normalizeError(error))
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({
          resolve: (token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            resolve(api(originalRequest))
          },
          reject,
        })
      })
    }

    if (!_refreshToken) {
      _onLogout?.()
      return Promise.reject(normalizeError(error))
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const { data } = await axios.post('/api/auth/refresh', null, {
        headers: { Authorization: `Bearer ${_refreshToken}` },
      })

      const newAccess = data.access_token
      const newRefresh = data.refresh_token
      setTokens(newAccess, newRefresh)

      processPendingRequests(null, newAccess)

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccess}`
      }
      return api(originalRequest)
    } catch (refreshError) {
      processPendingRequests(refreshError, null)
      clearTokens()
      _onLogout?.()
      return Promise.reject(normalizeError(error))
    } finally {
      isRefreshing = false
    }
  },
)

interface NormalizedError {
  message: string
  status: number
  code: string
}

function normalizeError(error: AxiosError): NormalizedError {
  if (error.response) {
    const data = error.response.data as Record<string, unknown> | undefined
    return {
      message: (data?.detail as string) || (data?.message as string) || error.message,
      status: error.response.status,
      code: (data?.code as string) || 'HTTP_ERROR',
    }
  }
  if (error.request) {
    return { message: 'Network error — could not reach the server', status: 0, code: 'NETWORK_ERROR' }
  }
  return { message: error.message || 'Unknown error', status: 0, code: 'UNKNOWN_ERROR' }
}
